import React, {useState, useEffect, useCallback} from 'react';
import { Tooltip, CircularProgress, IconButton } from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import {
  setFavoriteLocation,
  getIsFavoriteLocation
} from './requests/location-requests';
import {KeyValueStatelessObservable} from '../../util/observers/KeyValueStatelessObservable';
import {getErrorMessage} from '../../util/NetworkErrorUtil';
import {useSnackbar} from "../../context/snackbar-context";

type LocationFavoriteSetting = 'SET_FAVORITE' | 'UNSET_FAVORITE';

const LocationFavoriteCommunication: Record<LocationFavoriteSetting, { value: boolean; error: string }> = Object.freeze({
  SET_FAVORITE: {
    value: true,
    error: 'Error setting favorite location: ',
  },
  UNSET_FAVORITE: {
    value: false,
    error: 'Error unsetting favorite location: ',
  }
});

interface LocationFavoriteObserverPayload {
  loading?: boolean;
  isLocationFavorite?: boolean;
}

  // This Observer allows multiple icons for the same location to exist on the same page and act synchronously
const observableLocationFavorite = new KeyValueStatelessObservable<LocationFavoriteObserverPayload>();

interface LocationFavoriteIconProps {
  locationId: string;
  isFavorite?: boolean;
  setFavorite?: (value: boolean) => void;
}

const LocationFavoriteIcon = (props: LocationFavoriteIconProps) => {
  const {
    locationId,
    isFavorite,
    setFavorite
  } = props;

  const [isLocationFavorite, setIsLocationFavorite] = useState(false);
  const [loading, setLoading] = useState(false);

  const { snackbarShowMessage } = useSnackbar();

  useEffect(() => {
    // Define the observer function
    const observer = (observerPayload: LocationFavoriteObserverPayload) => {
      if (Object.prototype.hasOwnProperty.call(observerPayload, 'loading')) setLoading(observerPayload.loading as boolean);
      if (Object.prototype.hasOwnProperty.call(observerPayload, 'isLocationFavorite')) setIsLocationFavorite(observerPayload.isLocationFavorite as boolean);
    };

    // Subscribe when the component mounts
    observableLocationFavorite.subscribe(locationId, observer);

    // Return a cleanup function to unsubscribe when the component unmounts
    return () => {
      observableLocationFavorite.unsubscribe(locationId, observer);
    };
  }, [locationId]);

  const setLoadingValue = useCallback((value: boolean) => {
    setLoading(value);
    observableLocationFavorite.notify(locationId, { loading: value });
  },[locationId]);

  const setIsLocationFavoriteValue = useCallback((value: boolean) => {
    setIsLocationFavorite(value);
    observableLocationFavorite.notify(locationId, { isLocationFavorite: value });
  }, [locationId]);

  useEffect(() => {
    if (isFavorite !== true && isFavorite !== false) {
      setLoadingValue(true);
      getIsFavoriteLocation(locationId).then((resp) => {
        setIsLocationFavoriteValue(!!resp?.body?.favorite);
      }).catch(() => {
        setIsLocationFavoriteValue(false);
      }).finally(() => {
        setLoadingValue(false);
      });
    } else {
      setIsLocationFavoriteValue(isFavorite);
    }
  }, [isFavorite, locationId, setIsLocationFavoriteValue, setLoadingValue]);


  const toggleFavoriteLocation = async (event: React.MouseEvent, setting: LocationFavoriteSetting) => {
    event?.stopPropagation && event.stopPropagation();
    event?.preventDefault && event.preventDefault();
    setLoadingValue(true);
    try {
      const value = LocationFavoriteCommunication[setting].value;
      await setFavoriteLocation(locationId, value);
      setIsLocationFavoriteValue(value);
      setFavorite && setFavorite(value);
    } catch (error) {
      // Bug fix: the raw error object was previously passed as the `severity` argument
      // and never surfaced in the message. Use the standard error-message helper instead.
      snackbarShowMessage(getErrorMessage(error, LocationFavoriteCommunication[setting].error), 'error');
    }
    setLoadingValue(false);
  };

  return (
    loading ?
      <IconButton color="primary" onClick={(e) => e?.preventDefault()} >
        <CircularProgress color="primary" size="1.25rem" />
      </IconButton>
    : isLocationFavorite ?
      <Tooltip title="Click to un-favorite this location">
        <IconButton color="primary" onClick={(e) => toggleFavoriteLocation(e, 'UNSET_FAVORITE')} >
          <FavoriteIcon />
        </IconButton>
      </Tooltip>
    :
      <Tooltip title="Click to favorite this location">
        <IconButton color="primary" onClick={(e) => toggleFavoriteLocation(e, 'SET_FAVORITE')} >
          <FavoriteBorderIcon />
        </IconButton>
      </Tooltip>
  )
}

export default LocationFavoriteIcon;
